import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { tokenStorage } from "@/auth/tokenStorage";
import { openSseStream } from "@/lib/sseClient";

export interface DashboardContext {
  date_from?: string;
  date_to?: string;
  team_id?: string;
  agent_id?: string;
}

export interface HitlDecision {
  decision: "approve" | "reject";
  comment?: string;
  tool_input_override?: Record<string, unknown>;
}

/** Bloco renderizavel no chat do agente de observabilidade. */
export type ObsBlock =
  | {
      kind: "bubble";
      key: string;
      role: "user" | "assistant";
      content: string;
      streaming?: boolean;
    }
  | {
      kind: "tool_call";
      key: string;
      toolName: string;
      args: Record<string, unknown>;
    }
  | { kind: "tool_result"; key: string; outputPreview: string }
  | {
      kind: "hitl";
      key: string;
      hitlRequestId: string;
      toolName: string;
      toolInput: Record<string, unknown>;
      resolved?: boolean;
      decision?: "approve" | "reject";
      comment?: string;
    }
  | {
      kind: "artifact";
      key: string;
      filename: string;
      mimeType: string;
      contentBase64: string;
      sizeBytes: number;
    };

interface Turn {
  role: "user" | "assistant";
  content: string;
}

function rid(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Estado efemero do chat do agente de observabilidade. Tool-calling com HITL:
 * cada tool pede aprovacao (``hitl_requested``) e o usuario decide via
 * ``decide`` (resume no mesmo ``run_id``). Entre turnos, so o texto
 * user/assistant eh reenviado (o estado das tools de um turno vive no
 * backend/checkpointer). ``reset`` limpa tudo ("nova conversa").
 */
export function useObservabilityAgent() {
  const { t } = useTranslation("observabilityAgent");
  const [blocks, setBlocks] = useState<ObsBlock[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [pendingHitl, setPendingHitl] = useState(false);

  const turnsRef = useRef<Turn[]>([]);
  const assistantTextRef = useRef("");
  const currentBubbleRef = useRef<string | null>(null);
  const pendingHitlRef = useRef(false);
  const runIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const closeBubble = useCallback(() => {
    const cur = currentBubbleRef.current;
    if (!cur) return;
    setBlocks((prev) =>
      prev.map((b) =>
        b.kind === "bubble" && b.key === cur ? { ...b, streaming: false } : b,
      ),
    );
    currentBubbleRef.current = null;
  }, []);

  const appendToken = useCallback((text: string) => {
    if (!text) return;
    assistantTextRef.current += text;
    const cur = currentBubbleRef.current;
    if (!cur) {
      const key = rid("asst");
      currentBubbleRef.current = key;
      setBlocks((prev) => [
        ...prev,
        {
          kind: "bubble",
          key,
          role: "assistant",
          content: text,
          streaming: true,
        },
      ]);
    } else {
      setBlocks((prev) =>
        prev.map((b) =>
          b.kind === "bubble" && b.key === cur
            ? { ...b, content: b.content + text }
            : b,
        ),
      );
    }
  }, []);

  const finish = useCallback(() => {
    setStreaming(false);
    abortRef.current = null;
    closeBubble();
    // Turno so encerra (commit do texto do assistente no historico) quando
    // nao ha HITL pendente. Com HITL pendente, aguardamos a decisao.
    if (!pendingHitlRef.current) {
      const text = assistantTextRef.current.trim();
      if (text) {
        turnsRef.current = [
          ...turnsRef.current,
          { role: "assistant", content: text },
        ];
      }
      assistantTextRef.current = "";
    }
  }, [closeBubble]);

  const handleEvent = useCallback(
    (ev: { event: string; data: unknown }) => {
      const data = (ev.data ?? {}) as Record<string, unknown>;
      switch (ev.event) {
        case "meta":
          runIdRef.current = String(data.run_id ?? "") || null;
          break;
        case "token":
          appendToken(String(data.content ?? ""));
          break;
        case "tool_call":
          closeBubble();
          setBlocks((prev) => [
            ...prev,
            {
              kind: "tool_call",
              key: `tc-${String(data.tool_call_id ?? rid("tc"))}`,
              toolName: String(data.tool_name ?? ""),
              args: (data.args as Record<string, unknown>) ?? {},
            },
          ]);
          break;
        case "tool_result":
          closeBubble();
          setBlocks((prev) => [
            ...prev,
            {
              kind: "tool_result",
              key: `tr-${String(data.tool_call_id ?? rid("tr"))}`,
              outputPreview: String(data.output_preview ?? ""),
            },
          ]);
          break;
        case "hitl_requested": {
          closeBubble();
          pendingHitlRef.current = true;
          setPendingHitl(true);
          const id = String(data.hitl_request_id ?? "");
          setBlocks((prev) => [
            ...prev,
            {
              kind: "hitl",
              key: `hitl-${id}`,
              hitlRequestId: id,
              toolName: String(data.tool_name ?? ""),
              toolInput: (data.tool_input as Record<string, unknown>) ?? {},
              resolved: false,
            },
          ]);
          break;
        }
        case "hitl_resolved": {
          const id = String(data.hitl_request_id ?? "");
          pendingHitlRef.current = false;
          setPendingHitl(false);
          setBlocks((prev) =>
            prev.map((b) =>
              b.kind === "hitl" && b.hitlRequestId === id
                ? {
                    ...b,
                    resolved: true,
                    decision: data.decision as "approve" | "reject",
                    comment:
                      typeof data.comment === "string"
                        ? data.comment
                        : undefined,
                  }
                : b,
            ),
          );
          break;
        }
        case "artifact":
          setBlocks((prev) => [
            ...prev,
            {
              kind: "artifact",
              key: rid("art"),
              filename: String(data.filename ?? "relatorio.pdf"),
              mimeType: String(data.mime_type ?? "application/octet-stream"),
              contentBase64: String(data.content_base64 ?? ""),
              sizeBytes: Number(data.size_bytes ?? 0),
            },
          ]);
          break;
        case "error":
          toast.error(String(data.message ?? t("error")));
          break;
        case "done":
          finish();
          break;
        default:
          break;
      }
    },
    [appendToken, closeBubble, finish, t],
  );

  const openStream = useCallback(
    async (path: string, body: unknown) => {
      const token = await tokenStorage.getToken();
      abortRef.current = openSseStream(
        { path, method: "POST", body },
        {
          token,
          onEvent: handleEvent,
          onClose: finish,
          onError: (err) => {
            console.error("Obs agent SSE error", err);
            toast.error(t("error"));
            finish();
          },
        },
      );
    },
    [handleEvent, finish, t],
  );

  const send = useCallback(
    (text: string, dashboard?: DashboardContext) => {
      const trimmed = text.trim();
      if (!trimmed || streaming || pendingHitlRef.current) return;
      setStreaming(true);
      assistantTextRef.current = "";
      currentBubbleRef.current = null;
      runIdRef.current = null;
      turnsRef.current = [
        ...turnsRef.current,
        { role: "user", content: trimmed },
      ];
      setBlocks((prev) => [
        ...prev,
        { kind: "bubble", key: rid("user"), role: "user", content: trimmed },
      ]);
      void openStream("/observability-agent/chat", {
        messages: turnsRef.current,
        dashboard,
      });
    },
    [streaming, openStream],
  );

  const decide = useCallback(
    (hitlRequestId: string, payload: HitlDecision) => {
      if (streaming) return;
      const runId = runIdRef.current;
      if (!runId) {
        toast.error(t("error"));
        return;
      }
      setStreaming(true);
      pendingHitlRef.current = false;
      setPendingHitl(false);
      currentBubbleRef.current = null;
      void openStream(`/observability-agent/${runId}/decide`, {
        decision: payload.decision,
        tool_call_id: hitlRequestId,
        comment: payload.comment,
        tool_input_override: payload.tool_input_override,
      });
    },
    [streaming, openStream, t],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    turnsRef.current = [];
    assistantTextRef.current = "";
    currentBubbleRef.current = null;
    pendingHitlRef.current = false;
    runIdRef.current = null;
    setStreaming(false);
    setPendingHitl(false);
    setBlocks([]);
  }, []);

  return {
    blocks,
    streaming,
    canSend: !streaming && !pendingHitl,
    send,
    decide,
    reset,
  };
}
